import React, { useCallback, useEffect, useMemo, useState } from 'react';

export type GadgetStatus = 'loading' | 'done' | 'failed';

export interface GadgetReadiness {
  id: string;
  name: string;
  status: GadgetStatus;
}

// What a scheduled render is waiting on, read by the print screens and (via
// window.__fleetopsRenderStatus) the renderer
export interface RenderStatus {
  // Gadgets whose requests are in flight
  loading: string[];
  // Gadgets whose request failed
  failed: string[];
  // Grid gadgets that haven't registered their CSV yet
  pendingCsvs: string[];
}

interface RenderReadinessContextType {
  isTracking: boolean;
  gadgets: Record<string, GadgetReadiness>;
  status: RenderStatus;
  reportGadget: (gadget: GadgetReadiness) => void;
  removeGadget: (id: string) => void;
  setPendingCsvs: (names: string[]) => void;
}

// Safe no-ops outside a print route: gadgets report wherever they render, and
// only a scheduled render tracks them
export const RenderReadinessContext =
  React.createContext<RenderReadinessContextType>({
    isTracking: false,
    gadgets: {},
    status: { loading: [], failed: [], pendingCsvs: [] },
    reportGadget: () => {},
    removeGadget: () => {},
    setPendingCsvs: () => {},
  });

/**
 * Tracks each gadget's data requests on a scheduled render (the print
 * routes), so readiness is the gadgets saying they're done rather than the page
 * guessing from its skeletons. Gadgets report through GadgetStatusBoundary.
 */
const RenderReadinessProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const [gadgets, setGadgets] = useState<Record<string, GadgetReadiness>>({});
  const [pendingCsvs, setPendingCsvsState] = useState<string[]>([]);

  const reportGadget = useCallback((gadget: GadgetReadiness) => {
    setGadgets((current) => {
      const existing = current[gadget.id];
      if (
        existing &&
        existing.status === gadget.status &&
        existing.name === gadget.name
      ) {
        return current;
      }
      return { ...current, [gadget.id]: gadget };
    });
  }, []);

  const removeGadget = useCallback((id: string) => {
    setGadgets((current) => {
      if (!current[id]) {
        return current;
      }
      const { [id]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const setPendingCsvs = useCallback((names: string[]) => {
    setPendingCsvsState((current) =>
      current.length === names.length &&
      current.every((name, index) => name === names[index])
        ? current
        : names,
    );
  }, []);

  const status = useMemo((): RenderStatus => {
    const list = Object.values(gadgets);
    return {
      loading: list.filter((g) => g.status === 'loading').map((g) => g.name),
      failed: list.filter((g) => g.status === 'failed').map((g) => g.name),
      pendingCsvs,
    };
  }, [gadgets, pendingCsvs]);

  // The renderer reads this to wait on, and to log, exactly which gadgets are
  // missing
  useEffect(() => {
    window.__fleetopsRenderStatus = status;
  }, [status]);
  useEffect(
    () => () => {
      window.__fleetopsRenderStatus = undefined;
    },
    [],
  );

  const value = useMemo(
    () => ({
      isTracking: true,
      gadgets,
      status,
      reportGadget,
      removeGadget,
      setPendingCsvs,
    }),
    [gadgets, status, reportGadget, removeGadget, setPendingCsvs],
  );

  return (
    <RenderReadinessContext.Provider value={value}>
      {children}
    </RenderReadinessContext.Provider>
  );
};

export default RenderReadinessProvider;
